import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAdminFilters } from "@/contexts/AdminFiltersContext";
import {
  Users, Shield, AtSign, HardDrive, Ban, UserPlus,
  Loader2, Play, Clock, Wifi, Mail, Forward, ShieldOff, Inbox,
} from "lucide-react";

const CATEGORY_COLORS = {
  pro: "bg-blue-50 text-blue-600 border-blue-200",
  users: "bg-[#F5F5F5] text-[#555555] border-[#E5E5E5]",
  operations: "bg-amber-50 text-amber-600 border-amber-200",
  abuse: "bg-red-50 text-red-600 border-red-200",
};

export default function AdminOverview() {
  const { hideTestData } = useAdminFilters();
  const emQ = hideTestData ? "&exclude_em=1" : "";
  const emQFirst = hideTestData ? "?exclude_em=1" : "";
  const [stats, setStats] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [onlineData, setOnlineData] = useState(null);
  const [queryResult, setQueryResult] = useState(null);
  const [activeQuery, setActiveQuery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queryLoading, setQueryLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/admin/stats${emQFirst}`),
      api.get("/admin/queries/catalog"),
      api.get(`/admin/users-online?minutes=10${emQ}`),
    ]).then(([statsRes, catalogRes, onlineRes]) => {
      setStats(statsRes.data);
      setCatalog(catalogRes.data.queries || []);
      setOnlineData(onlineRes.data);
    }).catch(() => toast.error("Failed to load dashboard"))
      .finally(() => setLoading(false));
  }, [emQ, emQFirst]);

  // Refresh online count every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      api.get(`/admin/users-online?minutes=10${emQ}`).then(({ data }) => setOnlineData(data)).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [emQ]);

  const runQuery = useCallback(async (queryId) => {
    setQueryLoading(true);
    setActiveQuery(queryId);
    try {
      const { data } = await api.post("/admin/queries/run", { query_id: queryId });
      setQueryResult(data);
    } catch {
      toast.error("Query failed");
    } finally {
      setQueryLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={20} className="animate-spin text-[#888888]" />
      </div>
    );
  }

  const s = stats;

  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-8 max-w-[1100px]" data-testid="admin-overview">
        {/* Header */}
        <div>
          <h2 className="text-xl font-semibold text-[#111111] tracking-tight" data-testid="admin-overview-title">Operations</h2>
          <p className="text-sm text-[#888888] mt-0.5">Key metrics, live awareness, quick queries</p>
        </div>

        {/* Users Online Widget */}
        {onlineData && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-500/10 p-4" data-testid="users-online-widget">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-sm font-medium text-emerald-600">
                  {onlineData.online_count} user{onlineData.online_count !== 1 ? "s" : ""} online
                </span>
                <span className="text-[10px] text-emerald-600 font-mono">last {onlineData.window_minutes}min</span>
              </div>
              <Wifi size={14} className="text-emerald-500" />
            </div>
            {onlineData.recent_active?.length > 0 && (
              <div className="space-y-1.5">
                {onlineData.recent_active.slice(0, 5).map((u, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-[9px] font-medium text-emerald-600 shrink-0">
                        {(u.name || u.email || "?").charAt(0).toUpperCase()}
                      </div>
                      <span className="text-emerald-600 truncate max-w-[200px]">{u.name || u.email}</span>
                      <span className="text-emerald-600 font-mono text-[10px] truncate max-w-[150px]">{u.email}</span>
                    </div>
                    <span className="text-[10px] text-emerald-500 shrink-0">{relTime(u.last_seen_at)}</span>
                  </div>
                ))}
                {onlineData.online_count > 5 && (
                  <p className="text-[10px] text-emerald-500 mt-1">+{onlineData.online_count - 5} more</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-stats-grid">
          <StatCard icon={Wifi} label="Online Now" value={s?.users?.online ?? 0} sub="last 10 min" color="bg-emerald-50 text-emerald-600" />
          <StatCard icon={Users} label="Total Users" value={s?.users?.total ?? 0} sub={`+${s?.users?.new_7d ?? 0} this week`} color="bg-blue-50 text-blue-600" />
          <StatCard icon={Shield} label="PRO Active" value={s?.users?.pro_active ?? 0} color="bg-violet-50 text-violet-600" />
          <StatCard icon={Ban} label="Blocked" value={s?.users?.blocked ?? 0} color={s?.users?.blocked > 0 ? "bg-red-50 text-red-600" : "bg-[#F5F5F5] text-[#888888]"} />
          <StatCard icon={UserPlus} label="New Today" value={s?.users?.new_today ?? 0} sub={`${s?.users?.new_30d ?? 0} this month`} color="bg-cyan-50 text-cyan-600" />
          <StatCard
            icon={Inbox}
            label="Free Inboxes"
            value={s?.free_inbox?.total ?? 0}
            sub={`${s?.free_inbox?.active ?? 0} active · ${s?.free_inbox?.expired ?? 0} expired`}
            color="bg-orange-50 text-orange-600"
            data-testid="admin-stat-free-inboxes"
          />
          <StatCard icon={AtSign} label="Aliases" value={s?.aliases?.total ?? 0} sub={`${s?.aliases?.avg_per_user ?? 0} avg/user`} color="bg-amber-50 text-amber-600" />
          <StatCard icon={Mail} label="Emails" value={s?.emails?.total ?? 0} color="bg-indigo-50 text-indigo-600" />
          <StatCard icon={HardDrive} label="Storage" value={`${s?.storage?.total_mb ?? 0} MB`} sub={`${s?.storage?.total_files ?? 0} files`} color="bg-[#F5F5F5] text-[#888888]" />
        </div>

        {/* Top Storage */}
        {s?.storage?.top_users?.length > 0 && (
          <div className="bg-white border border-[#E5E5E5] rounded-lg p-4">
            <h3 className="text-xs font-mono uppercase tracking-widest text-[#888888] mb-3">Top Storage</h3>
            <div className="space-y-1.5">
              {s.storage.top_users.map((u, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="font-mono text-xs text-[#888888] truncate max-w-[250px]">{u.alias}</span>
                  <span className="text-xs text-[#888888] tabular-nums">{u.mb} MB</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Queries */}
        <div data-testid="admin-queries-section">
          <h3 className="text-sm font-semibold text-[#111111] mb-3">Quick Queries</h3>
          <div className="flex flex-wrap gap-2">
            {catalog.map((q) => (
              <Button
                key={q.id}
                variant="outline"
                size="sm"
                disabled={queryLoading}
                onClick={() => runQuery(q.id)}
                className={`text-xs gap-1.5 h-8 ${activeQuery === q.id ? "ring-2 ring-blue-500 ring-offset-1" : ""}`}
                data-testid={`query-btn-${q.id}`}
              >
                <Play size={10} className={queryLoading && activeQuery === q.id ? "animate-spin" : ""} />
                {q.label}
                <Badge variant="outline" className={`text-[8px] ml-1 px-1.5 py-0 ${CATEGORY_COLORS[q.category] || ""}`}>
                  {q.category}
                </Badge>
              </Button>
            ))}
          </div>
        </div>

        {/* Query Results */}
        {queryResult && (
          <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden" data-testid="query-results">
            <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[#111111]">{queryResult.label}</h3>
                <p className="text-xs text-[#888888]">{queryResult.count} results</p>
              </div>
              <Button variant="ghost" size="sm" className="text-xs text-[#888888] h-7" onClick={() => setQueryResult(null)}>
                Clear
              </Button>
            </div>

            {queryResult.count === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-[#888888]">No results</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="query-results-table">
                  <thead className="bg-[#F5F5F5]">
                    <tr>
                      {queryResult.columns.map((col) => (
                        <th key={col.key} className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-[#888888] whitespace-nowrap">
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {queryResult.results.map((row, i) => (
                      <tr key={i} className="hover:bg-[#F7F7F7]">
                        {queryResult.columns.map((col) => (
                          <td key={col.key} className="px-3 py-2 text-xs text-[#555555] whitespace-nowrap">
                            <CellValue value={row[col.key]} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="p-4 rounded-lg border border-[#E5E5E5] bg-white" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center mb-2 ${color}`}>
        <Icon size={15} />
      </div>
      <p className="text-2xl font-semibold text-[#111111] tabular-nums">{value}</p>
      <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888] mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-[#888888] mt-1">{sub}</p>}
    </div>
  );
}

function CellValue({ value }) {
  if (value === true) return <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px]">Yes</Badge>;
  if (value === false) return <Badge className="bg-[#F5F5F5] text-[#888888] border-[#E5E5E5] text-[9px]">No</Badge>;
  if (value === null || value === undefined || value === "\u2014") return <span className="text-[#555555]">\u2014</span>;

  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
    return <span className="flex items-center gap-1"><Clock size={10} className="text-[#888888]" />{relTime(str)}</span>;
  }
  return <span>{str}</span>;
}

function relTime(val) {
  if (!val) return "\u2014";
  try {
    const d = new Date(val);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return `${Math.floor(diffH / 24)}d ago`;
  } catch {
    return String(val);
  }
}
